import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { programRoundLabel } from "@heraion/shared";

import { Button } from "../primitives/Button";
import { Dialog } from "../primitives/Dialog";
import type { UiTheme } from "../theme";
import type { RoundLock } from "./useRoundLock";
import type { V3Styles } from "./v3Styles";

// «Done up to W<n> · Unlock» under the top bar, only while a program in
// progress has rounds locked (#336 slice 5, decision 4): the same words as the
// refusal's toast. «Unlock» asks first and says what changes in the history.
// A long translation or a large text scale wraps «Unlock» to two lines and
// the sentence beside it shrinks.
export function RoundLockBar({ theme, styles, lock }: { theme: UiTheme; styles: V3Styles; lock: RoundLock }) {
  const { t } = useTranslation();
  if (lock.marked < 0 || lock.unlocked) return null;
  const round = programRoundLabel(lock.marked + 1);
  const rounds =
    lock.marked === 0
      ? round
      : t("editorV3.lock.range", { from: programRoundLabel(1), to: round, defaultValue: "{{from}}-{{to}}" });
  const local = makeStyles(theme);
  return (
    <View style={local.bar}>
      <Text style={local.text}>
        {t("editorV3.rule.lockedStay", { round, defaultValue: "Done up to {{round}}: those rounds stay as they are." })}
      </Text>
      <Button
        theme={theme}
        variant="secondary"
        hitSlop={{ top: 8, bottom: 8 }}
        numberOfLines={2}
        style={local.unlock}
        label={t("editorV3.lock.unlock", { defaultValue: "Unlock" })}
        accessibilityLabel={t("editorV3.lock.unlockA11y", { defaultValue: "Unlock the rounds already done" })}
        onPress={lock.ask}
      />
      <Dialog
        theme={theme}
        visible={lock.asking}
        onRequestClose={lock.cancel}
        title={t("editorV3.lock.title", { defaultValue: "Unlock the rounds done?" })}
      >
        <Text style={local.body}>
          {t("editorV3.lock.body", {
            rounds,
            defaultValue:
              "The sessions already done read their prescription from the program: changing {{rounds}} changes what the history says was prescribed. They stay unlocked until you close the editor.",
          })}
        </Text>
        <View style={styles.dialogActions}>
          <Button theme={theme} variant="ghost" label={t("program.cancel", { defaultValue: "Cancel" })} onPress={lock.cancel} />
          <Button theme={theme} variant="danger" label={t("editorV3.lock.unlock", { defaultValue: "Unlock" })} onPress={lock.unlock} />
        </View>
      </Dialog>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    bar: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s3,
      paddingHorizontal: s.s5,
      paddingVertical: s.s2,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: p.border,
      backgroundColor: p.surface2,
    },
    text: { flex: 1, minWidth: 0, color: p.ink, fontSize: ty.sizes.sm },
    unlock: { flexShrink: 1, maxWidth: "50%" },
    body: { color: p.ink2, fontSize: ty.sizes.base },
  });
}
